import React, { useContext, useMemo, useCallback, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeCategoryPositionContext,
	IChangeCategoryPositionContext,
} from '../../../../changeCategoryPositionState/changeCategoryPositionContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { getCustomersShortInfo } from 'backend/customers';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

export const CustomerSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDCustomer, IDSelectedCustomer } =
		useContext<IChangeCategoryPositionContext>(ChangeCategoryPositionContext);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
		hasAccess ? getCustomersShortInfo : null,
	);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	const customers = useMemo(() => {
		const items = hasAccess ? response?.data.customers : loggedUser.customers || [];
		const visibleItems = showArchived
			? items
			: items?.filter(
					(customer) => customer.status === EntityStatus.Active || customer.id === IDSelectedCustomer,
			  );

		return visibleItems?.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser, showArchived, IDSelectedCustomer]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<OutsideFormSelect
			containerClassName="!min-h-min w-px"
			className="!mt-0"
			testId="CustomerSelect"
			name="CustomerSelect"
			label="customerLabel"
			onChange={setIDCustomer}
			onRefetch={refetch}
			value={IDSelectedCustomer}
			options={customers}
			listItemHeight={customers?.length}
			disabled={loading}
			loading={loading}
			fetchError={responseError}
			withValidation={false}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};
